import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { state: cartState, openCart } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Scroll to top function for mobile navigation
  const handleMobileNavClick = () => {
    setIsMenuOpen(false);
    // Smooth scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const navItems = [
    { name: 'Home', href: '/' },
    { name: 'Products', href: '/products' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActiveRoute = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <>
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-black/95 backdrop-blur-xl border-b border-lime-400/30' 
          : 'bg-black/20 backdrop-blur-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-between h-24">
            
            {/* Logo Section */}
            <div className="flex-shrink-0 z-10">
              <Link to="/" className="group block">
                <div className="flex items-center">
                  <div className="relative">
                    <img 
                      src="/PBX Pickle Strike Vector Artwork.png" 
                      alt="PBX Pickle" 
                      className="h-20 w-auto transition-all duration-300 group-hover:scale-105"
                    />
                  </div>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2">
              <div className="flex space-x-1 bg-black/30 backdrop-blur-md rounded-full p-2 border border-lime-400/20">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`relative px-6 py-3 rounded-full font-semibold text-sm uppercase tracking-wide transition-all duration-300 ${
                      isActiveRoute(item.href)
                        ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/25'
                        : 'text-white hover:text-lime-400 hover:bg-white/5'
                    }`}
                  >
                    {item.name}
                    {isActiveRoute(item.href) && (
                      <div className="absolute inset-0 bg-lime-400 rounded-full blur opacity-30 -z-10"></div>
                    )}
                  </Link>
                ))}
              </div>
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center space-x-4">
              <Button 
                variant="ghost"
                size="sm"
                onClick={openCart}
                className="text-white hover:text-lime-400 hover:bg-lime-400/10 transition-all duration-300 relative"
              >
                <ShoppingBag className="w-5 h-5" />
                {cartState.itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-lime-400 text-black text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {cartState.itemCount}
                  </span>
                )}
              </Button>
              <Link to="/products">
                <Button className="bg-gradient-to-r from-lime-400 to-lime-300 text-black hover:from-lime-300 hover:to-lime-200 font-black px-6 py-3 rounded-full text-sm uppercase tracking-wide transition-all duration-300 hover:shadow-xl hover:scale-105">
                  SHOP NOW
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden relative w-10 h-10 flex items-center justify-center text-white hover:text-lime-400 transition-all duration-300 hover:bg-lime-400/10 rounded-full"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <div className="relative w-6 h-6">
                <span className={`absolute top-1/2 left-1/2 w-6 h-0.5 bg-current transform transition-all duration-300 ${
                  isMenuOpen ? 'rotate-45 -translate-x-1/2 -translate-y-1/2' : '-translate-x-1/2 -translate-y-2'
                }`}></span>
                <span className={`absolute top-1/2 left-1/2 w-6 h-0.5 bg-current transform transition-all duration-300 ${
                  isMenuOpen ? 'opacity-0' : '-translate-x-1/2 -translate-y-1/2'
                }`}></span>
                <span className={`absolute top-1/2 left-1/2 w-6 h-0.5 bg-current transform transition-all duration-300 ${
                  isMenuOpen ? '-rotate-45 -translate-x-1/2 -translate-y-1/2' : '-translate-x-1/2 translate-y-1'
                }`}></span>
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`lg:hidden fixed inset-0 z-40 transition-all duration-500 ${
        isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}>
        <div className="absolute inset-0 bg-black/95 backdrop-blur-xl"></div>
        
        {/* Mobile Menu Content */}
        <div className={`relative h-full flex flex-col justify-center items-center transform transition-all duration-500 px-6 ${
          isMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}>
          
          {/* Navigation Links */}
          <div className="flex flex-col space-y-3 mb-8">
            {navItems.map((item, index) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group text-center py-3 px-6 rounded-xl font-bold text-lg uppercase tracking-wide transition-all duration-300 ${
                  isActiveRoute(item.href)
                    ? 'text-black bg-lime-400 shadow-lg shadow-lime-400/25'
                    : 'text-white hover:text-lime-400 hover:bg-lime-400/10'
                }`}
                style={{ 
                  transitionDelay: `${index * 100}ms`
                }}
                onClick={handleMobileNavClick}
              >
                <span className="relative">
                  {item.name}
                  <div className={`absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-lime-400 rounded-full transition-all duration-300 ${
                    isActiveRoute(item.href) ? 'w-full' : 'group-hover:w-full'
                  }`}></div>
                </span>
              </Link>
            ))}
          </div>

          {/* Mobile CTA Section */}
          <div className="flex flex-col items-center space-y-3 px-4">
            <div className="flex gap-3 w-full">
              <Button 
                onClick={() => { openCart(); handleMobileNavClick(); }}
                className="flex-1 bg-gray-800 border border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black font-bold py-3 px-4 rounded-xl text-base transition-all duration-300 relative"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                CART
                {cartState.itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-lime-400 text-black text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {cartState.itemCount}
                  </span>
                )}
              </Button>
              <Link to="/products" className="flex-1">
                <Button 
                  className="w-full bg-gradient-to-r from-lime-400 to-lime-300 text-black hover:from-lime-300 hover:to-lime-200 font-bold py-3 px-4 rounded-xl text-base uppercase tracking-wide transition-all duration-300 hover:shadow-xl hover:scale-105"
                  onClick={handleMobileNavClick}
                >
                  SHOP
                </Button>
              </Link>
            </div>
            
            <div className="flex items-center space-x-3 text-gray-400 text-xs">
              <span>Free shipping on $75+</span>
              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
              <span>30-day returns</span>
            </div>
          </div>

          {/* Social Proof */}
          <div className="mt-6 text-center">
            <p className="text-lime-400 font-semibold text-xs uppercase tracking-wide">
              Trusted by 2,500+ Athletes
            </p>
            <div className="flex justify-center mt-1 space-x-1">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-1.5 h-1.5 bg-lime-400 rounded-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>


    </>
  );
};

export default Navigation;
